/**
 * Sblocco delle funzioni passpartout (STANDARD).
 *
 * Ogni funzione riservata al passpartout ha una CHIAVE stabile (es. `cassa.eliminaTutti`, lo stesso catalogo del
 * backend, `FunzionePasspartout`). Un passpartout può renderla disponibile a tutti, per la propria azienda o per tutte.
 * Questo store legge dal server cosa è sbloccato per l'azienda di chi è collegato:
 *
 *   GET    /api/v2/passpartout/funzioni                       chiavi effettive (tutti gli autenticati)
 *   GET    /api/v2/passpartout/funzioni/catalogo              catalogo con stato (solo passpartout)
 *   POST   /api/v2/passpartout/funzioni/{chiave}              sblocca (solo passpartout)  { ambito: AZIENDA | GLOBALE }
 *   DELETE /api/v2/passpartout/funzioni/{chiave}?ambito=...   richiude (solo passpartout)
 *
 * Il frontend NON decide nulla di sicurezza: `visibile()` serve solo a mostrare o nascondere; ogni operazione è
 * ricontrollata dal server. Le pagine usano `visibile(chiave)` al posto del solo `auth.me.passpartout`.
 */
import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import { apiClient } from '@/api/client'
import type { components } from '@/api/generated/schema'
import { useAuthStore } from '@/stores/auth'

export type AmbitoSblocco = 'AZIENDA' | 'GLOBALE'
export type FunzioniSbloccate = components['schemas']['FunzioniSbloccateDTO']
export type FunzioneCatalogo = components['schemas']['FunzioneCatalogoDTO']

const BASE = '/api/v2/passpartout/funzioni'

export const useFunzioniSbloccateStore = defineStore('funzioniSbloccate', () => {
  const auth = useAuthStore()
  const chiavi = ref<string[]>([])
  const sblocchi = ref<{ chiave?: string; ambito?: string }[]>([])
  const caricato = ref(false)
  const inCorso = ref<Promise<void> | null>(null)

  const isPasspartout = computed(() => auth.me?.passpartout === true)

  function applica(dto: FunzioniSbloccate | undefined | null) {
    chiavi.value = [...(dto?.chiavi ?? [])]
    sblocchi.value = [...(dto?.sblocchi ?? [])]
  }

  /** Si incrementa a ogni reset: una risposta arrivata dopo un cambio di persona/sessione non deve più essere applicata. */
  let generazione = 0

  function reset() {
    generazione++
    inCorso.value = null
    chiavi.value = []
    sblocchi.value = []
    caricato.value = false
  }

  /** Carica (una volta, o di nuovo con `forza`) le funzioni sbloccate per l'azienda di chi è collegato. */
  async function carica(forza = false): Promise<void> {
    if (!auth.me) return
    if (caricato.value && !forza) return
    if (inCorso.value && !forza) return inCorso.value
    const mia = generazione
    const richiesta = (async () => {
      try {
        const { data } = await apiClient.get<FunzioniSbloccate>(BASE)
        if (mia === generazione) {
          applica(data)
          caricato.value = true
        }
      } catch {
        // in caso di errore resta la sola regola «sei passpartout»: mai più permissivo.
        // `caricato` resta false: la navigazione successiva riprova (un tentativo per chiamata, nessun loop).
        if (mia === generazione) applica(null)
      } finally {
        if (mia === generazione) inCorso.value = null
      }
    })()
    inCorso.value = richiesta
    return richiesta
  }

  /** La funzione si può mostrare/usare: sei in sessione passpartout oppure è stata sbloccata per la tua azienda o per tutte. */
  function visibile(chiave: string): boolean {
    return isPasspartout.value || chiavi.value.includes(chiave)
  }

  /** Stato dello sblocco (dettaglio degli ambiti: lo conosce solo chi è passpartout). */
  function stato(chiave: string): { azienda: boolean; globale: boolean } {
    return {
      azienda: sblocchi.value.some((s) => s.chiave === chiave && s.ambito === 'AZIENDA'),
      globale: sblocchi.value.some((s) => s.chiave === chiave && s.ambito === 'GLOBALE'),
    }
  }

  async function sblocca(chiave: string, ambito: AmbitoSblocco): Promise<void> {
    const { data } = await apiClient.post<FunzioniSbloccate>(`${BASE}/${encodeURIComponent(chiave)}`, { ambito })
    applica(data)
  }

  async function blocca(chiave: string, ambito: AmbitoSblocco): Promise<void> {
    const { data } = await apiClient.delete<FunzioniSbloccate>(`${BASE}/${encodeURIComponent(chiave)}`, { params: { ambito } })
    applica(data)
  }

  async function catalogo(): Promise<FunzioneCatalogo[]> {
    const { data } = await apiClient.get<FunzioneCatalogo[]>(`${BASE}/catalogo`)
    return data
  }

  // Cambio di persona, di sessione (passpartout sì/no) o logout: lo stato in memoria non vale più.
  watch(
    () => [auth.me?.userAccountId ?? null, auth.me?.passpartout ?? null] as const,
    ([acc]) => {
      reset()
      if (acc !== null) void carica(true)
    },
    { immediate: true },
  )

  return { chiavi, sblocchi, caricato, isPasspartout, carica, visibile, stato, sblocca, blocca, catalogo, reset }
})
